import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type Report = {
  id: string;
  report_date: string;
  content_md: string | null;
  metrics: Record<string, any> | null;
  generated_at: string;
  sent_at: string | null;
  status: string;
};

export function AdminReportes() {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('nightly_reports')
      .select('id, report_date, content_md, metrics, generated_at, sent_at, status')
      .order('report_date', { ascending: false })
      .limit(30);
    setReports((data as any) ?? []);
    setLoading(false);
    if (data && data.length > 0 && !selectedId) setSelectedId((data[0] as any).id);
  }

  async function generateNow() {
    setGenerating(true);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nightly-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
    });
    setGenerating(false);
    load();
  }

  const sel = reports.find(r => r.id === selectedId);

  return (
    <AdminContainer
      eyebrow="Análisis"
      title="Reportes nocturnos AI"
      action={
        <button onClick={generateNow} disabled={generating} style={{
          background: theme.accent, color: theme.brand, border: 'none',
          padding: '10px 18px', fontSize: 12, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer',
          fontFamily: theme.font.sans, opacity: generating ? 0.5 : 1,
        }}>
          {generating ? 'Generando…' : '🤖 Generar ahora'}
        </button>
      }
    >
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 220px)' }}>
        <div style={{ width: 280, background: theme.bg, border: `1px solid ${theme.border}`, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, color: theme.textMuted, textAlign: 'center' }}>Cargando…</div>
          ) : reports.length === 0 ? (
            <div style={{ padding: 24, color: theme.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
              Sin reportes aún.<br />
              Cron diario corre 22:00 CDMX. O genera uno manual.
            </div>
          ) : reports.map(r => (
            <button key={r.id} onClick={() => setSelectedId(r.id)} style={{
              width: '100%',
              background: selectedId === r.id ? theme.bgSoft : theme.bg,
              border: 'none',
              borderBottom: `1px solid ${theme.border}`,
              borderLeft: selectedId === r.id ? `3px solid ${theme.accent}` : '3px solid transparent',
              padding: '12px 14px',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: theme.font.sans,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.brand }}>
                {new Date(r.report_date).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}
              </div>
              <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4, fontFamily: theme.font.mono }}>
                {r.metrics?.orders_today ?? 0} ped · ${(r.metrics?.sales_mxn_today ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN
              </div>
              <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>
                {r.metrics?.new_whatsapp_leads ?? 0} leads · 🔥 {r.metrics?.hot_leads ?? 0}
              </div>
            </button>
          ))}
        </div>

        <div style={{ flex: 1, background: theme.bg, border: `1px solid ${theme.border}`, padding: 24, overflowY: 'auto' }}>
          {!sel ? (
            <div style={{ color: theme.textMuted }}>Selecciona un reporte</div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 6 }}>
                Reporte · {sel.status}
              </div>
              <h2 style={{ fontFamily: theme.font.head, fontSize: 24, fontWeight: 400, color: theme.brand, margin: 0 }}>
                {new Date(sel.report_date).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </h2>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
                Generado: {new Date(sel.generated_at).toLocaleString('es-MX')}
              </div>

              {sel.metrics && (
                <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  <MetricCard label="Pedidos" value={sel.metrics.orders_today ?? 0} />
                  <MetricCard label="Ventas MXN" value={`$${(sel.metrics.sales_mxn_today ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`} />
                  <MetricCard label="Leads WhatsApp" value={sel.metrics.new_whatsapp_leads ?? 0} sub={`🔥 ${sel.metrics.hot_leads ?? 0} calientes`} />
                  <MetricCard label="Alertas inv." value={sel.metrics.inventory_alerts_count ?? 0} highlight={(sel.metrics.inventory_alerts_count ?? 0) > 0} />
                </div>
              )}

              {sel.content_md && (
                <pre style={{
                  marginTop: 24,
                  padding: 20,
                  background: theme.bgSoft,
                  border: `1px solid ${theme.border}`,
                  fontFamily: theme.font.sans,
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  color: theme.text,
                }}>
                  {sel.content_md}
                </pre>
              )}
            </>
          )}
        </div>
      </div>
    </AdminContainer>
  );
}

function MetricCard({ label, value, sub, highlight }: { label: string; value: any; sub?: string; highlight?: boolean }) {
  return (
    <div style={{
      background: highlight ? '#FEE2E2' : theme.bgSoft,
      border: `1px solid ${highlight ? '#DC2626' : theme.border}`,
      padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? '#DC2626' : theme.brand, marginTop: 4 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}
